import { useState, useEffect } from 'react';
import { MaterialSymbol } from '@/components/ui/material-symbol';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { NotificationList } from './NotificationList';
import { useRealtimeNotifications } from '@/hooks/useRealtimeNotifications';
import { apiFetch } from '@/lib/api-fetch';

interface NotificationDropdownProps {
  userId: string;
}

export function NotificationDropdown({ userId }: NotificationDropdownProps) {
  const [unreadCount, setUnreadCount] = useState(0);

  // Get real-time notifications
  const notifications = useRealtimeNotifications(userId);

  useEffect(() => {
    if (userId) {
      loadUnreadCount();
    }
  }, [userId, notifications]);

  const loadUnreadCount = async () => {
    try {
      const response = await apiFetch(`/api/notifications/unread-count?userId=${userId}`);
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.count || 0);
      }
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="relative touch-target rounded p-2 text-arc-text-muted transition-colors hover:bg-white/5 hover:text-arc-accent"
          aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        >
          <MaterialSymbol name="notifications" className="!text-[22px]" />
          {unreadCount > 0 && (
            <Badge variant="danger" className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-96 p-0">
        <div className="flex items-center justify-between p-4 border-b border-arc-border">
          <h3 className="font-semibold text-arc-text-main">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                try {
                  await apiFetch('/api/notifications/mark-all-read', { method: 'POST' });
                  loadUnreadCount();
                } catch (error) {
                  console.error('Failed to mark all as read:', error);
                }
              }}
            >
              Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="h-96">
          <NotificationList userId={userId} />
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

